import {Dispatch, useCallback} from 'react';
import {ActionTypes, AutomationAction} from '../reducer/actions';
import {AutomationContextType, AutomationFilter} from '../reducer/model';
import {RuleType} from '../enums';
import {AlertListItem} from '../reducer/types';
import {priority} from '@priority-software/priority-proto-api/lib';
import {fetchEditorMessageColumns, getCoverLetter} from '../actions';
import {serializedTreeResults} from '../../EmailEditor/utils/serializedTreeResults';
import {BusinessRuleAction} from '../constants';
import {isNewEmailTemplatesSupported} from '../actions/emails';

export const useAutomationCommonHandlers = (
    dispatch: Dispatch<AutomationAction>,
    state: AutomationContextType
) => {
    const {emailTemplateFields, automationType} = state;
    /* COMMON API */
    const setFilter = useCallback((data: AutomationFilter) => {
        dispatch({type: ActionTypes.SET_APPLIED_FILTERS, payload: {filter: data}});
    }, []);

    const removeFilter = useCallback((filterName: string, value: string | number) => {
        dispatch({type: ActionTypes.REMOVE_APPLIED_FILTERS, payload: {filterName, value}});
    }, []);

    const handleFilters = useCallback(() => {
        dispatch({type: ActionTypes.HANDLE_RULE_FILTERS});
    }, []);

    const handleAutomationOpen = useCallback(
        () => dispatch({type: ActionTypes.OPEN_AUTOMATION}),
        []
    );

    const handleDialogType = useCallback(
        (type: RuleType) =>
            dispatch({
                type: ActionTypes.SET_AUTOMATION_TYPE,
                payload: {type}
            }),
        []
    );

    const getEmailTemplateFields = useCallback(async () => {
        if (!emailTemplateFields) {
            const treeBranchesRes = await fetchEditorMessageColumns();

            if (treeBranchesRes?.ColumnsTree) {
                dispatch({
                    type: ActionTypes.SET_EMAIL_TEMPLATE_FIELDS,
                    payload: {
                        emailTemplateFields: serializedTreeResults(
                            treeBranchesRes?.ColumnsTree,
                            true
                        )
                    }
                });
            }
        }
    }, [emailTemplateFields]);

    const openRuleDialog = useCallback(
        async (ruleNumber?: number, fetchHtml?: boolean) => {
            const newEditorSupport = isNewEmailTemplatesSupported();

            const payload = {ruleNumber, newEditorSupport} as {
                ruleNumber: number;
                CoverLetters?: priority.netitems.form.IAlertMessage[];
                newEditorSupport: boolean;
            };

            if (fetchHtml && newEditorSupport) {
                const [coverLetterResult] = await Promise.all([
                    getCoverLetter(ruleNumber, automationType),
                    getEmailTemplateFields()
                ]);

                if (coverLetterResult?.CoverLetters?.length) {
                    payload.CoverLetters = [...coverLetterResult.CoverLetters];
                }
            }
            dispatch({type: ActionTypes.OPEN_SINGLE_RULE, payload});
        },
        [emailTemplateFields, getEmailTemplateFields, automationType]
    );

    const closeSingleRuleDialog = useCallback(
        () => dispatch({type: ActionTypes.CLOSE_SINGLE_RULE}),
        []
    );

    const handleSearch = useCallback((searchValue: string = '') => {
        dispatch({type: ActionTypes.HANDLE_SEARCH, payload: {searchValue}});
        dispatch({type: ActionTypes.HANDLE_RULE_FILTERS});
    }, []);

    const setRules = useCallback((rules: Array<AlertListItem>) => {
        dispatch({type: ActionTypes.SET_AUTOMATION_RULES, payload: {rules}});
        dispatch({type: ActionTypes.HANDLE_RULE_FILTERS});
    }, []);

    const setSelectedRuleIndex = useCallback((index: number) => {
        dispatch({type: ActionTypes.SET_SELECTED_RULE_INDEX, payload: {index}});
    }, []);

    const updateOpenRule = useCallback(async (data: Partial<priority.netitems.form.IAlert>) => {
        dispatch({type: ActionTypes.UPDATE_OPEN_RULE, payload: {data}});
    }, []);

    const dispatchState = useCallback(async (data: Partial<AutomationContextType>) => {
        dispatch({type: ActionTypes.DISPATCH_STATE, payload: data});
    }, []);

    const handleAIRuleSuggestions = useCallback(async data => {
        dispatch({type: ActionTypes.SET_AI_RULE_SUGGESTION, payload: data});
    }, []);

    const getFullRuleInfo = useCallback(
        async (rule: priority.netitems.form.IAlert) => {
            const completeRule = {...rule};
            if (rule.MessageType === BusinessRuleAction.EmailMsg) {
                const supportHTMLEmails = isNewEmailTemplatesSupported();

                if (supportHTMLEmails) {
                    const result = await getCoverLetter(Number(rule.AlertNumber), automationType);
                    if (result?.CoverLetters?.length) {
                        completeRule.CoverLetters = [...result.CoverLetters];
                    }
                }
            }
            return completeRule;
        },
        [automationType]
    );

    return {
        getFullRuleInfo,
        setFilter,
        removeFilter,
        handleFilters,
        handleAutomationOpen,
        handleDialogType,
        openRuleDialog,
        closeSingleRuleDialog,
        handleSearch,
        setRules,
        setSelectedRuleIndex,
        updateOpenRule,
        handleAIRuleSuggestions,
        dispatchState,
        getEmailTemplateFields
    };
};
